import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError, gql, useLazyQuery } from '@apollo/client';
import { useAuthContext } from 'contexts';
import { Sentry } from 'lib/sentry';
import { ExternalAccountType, HealthStatus } from 'generated/graphql';

export type CalendarResolution = 'reauthorize' | 'retry' | 'contact_support';

/**
 * One calendar provider the user should do something about. Covers both a
 * genuinely unhealthy account and a probe we could not complete — anything we
 * cannot confirm is fine belongs here.
 */
export type IntegrationNeedingAttention = {
  type: ExternalAccountType;
  /**
   * Server-provided explanation, or empty when we have nothing worth showing.
   * Empty means the caller should render its own localized copy — we never put
   * raw transport text in front of a user.
   */
  errorMessage: string;
  /**
   * The server's human-readable next step, when it gave one. Carries the whole
   * value of a `contact_support` row: without it that row has a title, a
   * message, and nothing the user can act on.
   */
  guidance: string;
  resolution: CalendarResolution;
};

/**
 * The three honest answers this hook can give. Modelled as a sum type so
 * "we could not check at all" is representable: previous shapes had to
 * overload either the empty list (producing a false all-clear) or the loading
 * flag (producing a spinner that never resolves).
 */
export type CalendarHealth =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | {
      state: 'ready';
      needsAttention: IntegrationNeedingAttention[];
      /** At least one provider was hidden on an inferred not-connected. */
      uncertain: boolean;
    };

const QUERY_FIELD = 'getExternalAccountStatus';

// TODO(PEST-538): replace with the codegen'd operation types once
// `npm -w apps/shell generate-types-gql` has been run against a reachable
// schema (it needs VPN access to the schema host, so it could not be run when
// this landed). Named distinctly from the symbols codegen will emit so the two
// can never silently shadow each other.
//
// Shapes only the fields the document below actually selects. Deliberately not
// the generated `ExternalAccountStatus`: that type declares the whole schema
// object (`status.checks`, `error.category`, ...), none of which we ask for, so
// reusing it would let a future reader dereference a field that type-checks but
// is `undefined` at runtime.
type ProbeQueryData = {
  getExternalAccountStatus: {
    status: {
      health: HealthStatus;
      error:
        | {
            message: string;
            resolution:
              | { action: string; description: string }
              | null
              | undefined;
          }
        | null
        | undefined;
    };
  };
};

type ProbeQueryVariables = {
  userId: string;
  type: ExternalAccountType;
};

// A single document, issued once per provider with different variables. See
// the comment above the two `useLazyQuery` calls for why it must never be
// combined into one request.
const GET_EXTERNAL_ACCOUNT_STATUS = gql`
  query GetExternalAccountStatus($userId: ID!, $type: ExternalAccountType!) {
    getExternalAccountStatus(userId: $userId, type: $type) {
      status {
        health
        error {
          message
          resolution {
            action
            description
          }
        }
      }
    }
  }
`;

const VALID_RESOLUTIONS: ReadonlySet<CalendarResolution> = new Set([
  'reauthorize',
  'retry',
  'contact_support',
]);

// `resolution.action` is a free-form String in the schema, not an enum, so the
// backend is free to send a value we don't recognise (or omit it). Matching is
// case- and whitespace-insensitive because the only thing standing between
// "REAUTHORIZE" and every row silently collapsing to contact_support is this
// comparison. Anything still unrecognised falls back to contact_support, which
// avoids implying a self-service fix we haven't verified — and is reported, so
// contract drift surfaces here rather than in a support ticket.
function toResolution(
  action: string | undefined,
  onUnrecognised?: (action: string) => void
): CalendarResolution {
  const normalised = action?.trim().toLowerCase();
  if (VALID_RESOLUTIONS.has(normalised as CalendarResolution)) {
    return normalised as CalendarResolution;
  }
  if (normalised) {
    onUnrecognised?.(normalised);
  }
  return 'contact_support';
}

// How confident we are that a provider is simply not connected — the one case
// we hide entirely. `guessed` exists because hiding a provider on a hunch can
// reassure a user whose calendar is actually broken.
type NotConnectedVerdict = 'no' | 'certain' | 'guessed';

// A 404 here means the user never connected that provider: expected, not
// broken. Structured signals are preferred and treated as certain; the text
// fallback is scoped to errors raised on this query's field, because otherwise
// an unrelated "organization not found" would read as "never connected" for
// every provider at once. We never inspect `ApolloError.message`: it is just
// the concatenation of the errors below, and Apollo substitutes the literal
// "Error message not found." for a malformed entry, which would match.
function classifyNotConnected(error: ApolloError): NotConnectedVerdict {
  let verdict: NotConnectedVerdict = 'no';

  for (const graphQLError of error.graphQLErrors) {
    const extensions = graphQLError.extensions as
      | {
          code?: string;
          response?: { status?: number };
          exception?: { status?: number };
        }
      | undefined;
    const status =
      extensions?.response?.status ?? extensions?.exception?.status;
    const notFound = status === 404 || extensions?.code === 'NOT_FOUND';
    const raisedOnOurField = graphQLError.path?.[0] === QUERY_FIELD;

    if (notFound) {
      // Field scoping matters just as much here as on the text path below. A
      // gateway-level NOT_FOUND — a failed org lookup, a permission miss, a
      // resolver upstream of this field — is not evidence about this
      // provider, and treating it as certain would hide every provider at
      // once and render "all your connections are working" to a user whose
      // calendar is broken.
      if (raisedOnOurField) {
        return 'certain';
      }
      // No path at all: it probably concerns this query, since that is what
      // we asked for, but we cannot prove it. Hide the provider and let the
      // caller say it could not check everything, rather than claiming
      // certainty we do not have.
      if (graphQLError.path === undefined) {
        verdict = 'guessed';
      }
      // Raised on some other field: unrelated failure. Contribute nothing, so
      // the provider surfaces as retryable instead of silently vanishing.
      continue;
    }

    const message = graphQLError.message.toLowerCase();
    if (
      raisedOnOurField &&
      (message.includes('404') || message.includes('not found'))
    ) {
      verdict = 'guessed';
    }
  }

  return verdict;
}

type ProbeResult = {
  data?: ProbeQueryData | null;
  error?: ApolloError;
};

type ProviderOutcome = {
  integration: IntegrationNeedingAttention | null;
  // True when we hid this provider on the text fallback rather than a
  // structured signal.
  suppressedOnGuess: boolean;
};

// Turns one provider's probe result into a row, or nothing when there is
// nothing to act on (healthy, or never connected).
function toOutcome(
  type: ExternalAccountType,
  { data, error }: ProbeResult,
  onUnrecognisedResolution?: (action: string) => void
): ProviderOutcome {
  if (error) {
    const notConnected = classifyNotConnected(error);
    if (notConnected !== 'no') {
      return {
        integration: null,
        suppressedOnGuess: notConnected === 'guessed',
      };
    }
    // A probe that failed for any other reason must never be silently dropped —
    // that could hide a genuinely broken account. Surface it as retryable, with
    // no message so the caller renders localized copy, not transport text.
    return {
      integration: {
        type,
        errorMessage: '',
        guidance: '',
        resolution: 'retry',
      },
      suppressedOnGuess: false,
    };
  }

  const status = data?.getExternalAccountStatus?.status;
  if (!status) {
    // No error and no payload. Both are non-null in the schema so we should
    // never be here, but "unknown" must not be reported as "fine".
    return {
      integration: {
        type,
        errorMessage: '',
        guidance: '',
        resolution: 'retry',
      },
      suppressedOnGuess: false,
    };
  }

  if (status.health === HealthStatus.Healthy) {
    return { integration: null, suppressedOnGuess: false };
  }

  // Unhealthy with no detail: still worth telling the user about, and not
  // self-serviceable, so say so rather than dropping the row.
  if (!status.error) {
    return {
      integration: {
        type,
        errorMessage: '',
        guidance: '',
        resolution: 'contact_support',
      },
      suppressedOnGuess: false,
    };
  }

  return {
    integration: {
      type,
      errorMessage: status.error.message,
      guidance: status.error.resolution?.description ?? '',
      resolution: toResolution(
        status.error.resolution?.action,
        onUnrecognisedResolution
      ),
    },
    suppressedOnGuess: false,
  };
}

type OutcomeByProvider = Partial<Record<ExternalAccountType, ProviderOutcome>>;

type ProbeFn = (options: {
  variables: ProbeQueryVariables;
}) => Promise<ProbeResult>;

/**
 * Reports which of the current user's calendar integrations (Google, Microsoft
 * Graph) need attention, so the UI can prompt the user to reconnect. Healthy
 * and never-connected providers are left out; anything we could not confirm is
 * fine is reported rather than hidden.
 */
export function useCalendarIntegrationHealth({
  enabled,
}: {
  enabled: boolean;
}): {
  health: CalendarHealth;
  /** True while a re-probe is in flight, for the caller's pending state. */
  refetching: boolean;
  /** Re-probe every provider. */
  refetch: () => Promise<void>;
} {
  const { currentUser, loading: authLoading } = useAuthContext();
  const userId = currentUser?.id;

  // TWO reasons this is two instances of one document, both load-bearing:
  //
  // 1. Not one document with both providers aliased: `getExternalAccountStatus`
  //    is non-nullable and the API 404s for a provider the user never
  //    connected, so one 404 would null the whole query root and erase the
  //    other provider's result with it.
  // 2. Not one instance executed twice: Apollo's `executeQuery` reobserves the
  //    instance's single shared `ObservableQuery`, and its error path resolves
  //    from `observable.getCurrentResult()` — so two concurrent executes on one
  //    instance cross-resolve with each other's variables. Collapsing to one
  //    instance is the obvious-looking simplification; it silently swaps the
  //    providers' results.
  const [fetchGoogleStatus] = useLazyQuery<ProbeQueryData, ProbeQueryVariables>(
    GET_EXTERNAL_ACCOUNT_STATUS,
    { fetchPolicy: 'network-only' }
  );
  const [fetchMicrosoftStatus] = useLazyQuery<
    ProbeQueryData,
    ProbeQueryVariables
  >(GET_EXTERNAL_ACCOUNT_STATUS, { fetchPolicy: 'network-only' });

  const [outcomes, setOutcomes] = useState<OutcomeByProvider>({});
  const [settled, setSettled] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [refetching, setRefetching] = useState(false);

  // Identifies the newest full run. Responses from superseded runs are
  // discarded, so closing and reopening can't let an older answer win.
  const runIdRef = useRef(0);

  // Read inside `refetch` so a late caller can't publish into a disabled hook.
  // Kept in a ref rather than a dependency because `refetch` is handed to
  // callers, and rebuilding its identity whenever `enabled` flips would
  // invalidate every callback that holds it.
  const enabledRef = useRef(enabled);
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // One entry per provider, so adding a member to `ExternalAccountType` fails
  // to compile HERE — not just in the modal's display map — rather than the new
  // provider silently never being probed and so reporting healthy forever.
  // Declaration order is the order the UI renders.
  const fetchByProvider = useMemo(
    () =>
      ({
        [ExternalAccountType.Google]: fetchGoogleStatus,
        [ExternalAccountType.MicrosoftGraph]: fetchMicrosoftStatus,
      } satisfies Record<ExternalAccountType, ProbeFn>),
    [fetchGoogleStatus, fetchMicrosoftStatus]
  );

  const providerOrder = useMemo(
    () => Object.keys(fetchByProvider) as ExternalAccountType[],
    [fetchByProvider]
  );

  // Probes one provider. Returns null only if the call threw outright, which
  // Apollo's lazy execute does not do for network or GraphQL failures — those
  // resolve with an `error` field and become a retryable row in `toOutcome`.
  // This guards the narrow case of a synchronous throw out of `executeQuery`
  // (bad options/variables) so one malformed call can't take the run down.
  const probeOne = useCallback(
    async (type: ExternalAccountType, id: string) => {
      try {
        return toOutcome(
          type,
          await fetchByProvider[type]({ variables: { userId: id, type } }),
          // An action we don't recognise means the API's vocabulary has moved
          // and every affected row is quietly downgraded to "contact support".
          // Report it, or the first sign is a support ticket.
          (unrecognisedAction) =>
            Sentry.captureException(
              new Error(
                `Unrecognised calendar resolution action: ${unrecognisedAction}`
              ),
              {
                tags: { probeProvider: type },
                extra: { resolutionAction: unrecognisedAction },
              }
            )
        );
      } catch (error) {
        // The user only ever sees a generic retry row, so the cause has to be
        // reported or it is lost — on a feature whose whole job is diagnosing
        // broken calendar connections.
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          { tags: { probeProvider: type } }
        );
        return null;
      }
    },
    [fetchByProvider]
  );

  // Probes every provider and replaces the whole verdict.
  //
  // Refuses to publish anything while disabled. The epoch guard alone is not
  // enough: a caller can hold a reference to `refetch` and invoke it after the
  // hook is disabled — the reconnect modal does exactly this, when an OAuth
  // popup resolves after the user has closed the modal. That call would start
  // a *new* run, so it would not look stale, and it would leave `settled` true
  // with a verdict computed for a closed modal. Reopening would then show that
  // stale answer before the fresh probe replaced it.
  const refetch = useCallback(async (): Promise<void> => {
    if (!enabledRef.current) {
      return;
    }

    const runId = ++runIdRef.current;
    const isStale = (): boolean =>
      runId !== runIdRef.current || !enabledRef.current;

    if (!userId) {
      // No user to probe for. `health` reports this as `unavailable` only once
      // auth has finished — while it is still resolving we stay in `loading`,
      // so a slow sign-in doesn't flash an accusation about the user's calendar.
      setOutcomes({});
      setUnavailable(true);
      setRefetching(false);
      setSettled(true);
      return;
    }

    setRefetching(true);
    const results = await Promise.all(
      providerOrder.map((type) => probeOne(type, userId))
    );
    if (isStale()) {
      // A newer run owns the pending flag now; clearing it here would wipe
      // that run's spinner.
      return;
    }

    // Every probe threw: we know nothing, so say so rather than reporting an
    // empty list, which the UI would read as "everything is fine".
    if (results.every((outcome) => outcome === null)) {
      setOutcomes({});
      setUnavailable(true);
    } else {
      const next: OutcomeByProvider = {};
      providerOrder.forEach((type, index) => {
        const outcome = results[index];
        // A provider whose probe threw is unknown, not healthy.
        next[type] = outcome ?? {
          integration: {
            type,
            errorMessage: '',
            guidance: '',
            resolution: 'retry',
          },
          suppressedOnGuess: false,
        };
      });
      setOutcomes(next);
      setUnavailable(false);
    }
    setRefetching(false);
    setSettled(true);
  }, [userId, probeOne, providerOrder]);

  useEffect(() => {
    if (!enabled) {
      // Abandon any in-flight run and reset, so the next enable reports loading
      // again rather than briefly reusing the previous verdict.
      runIdRef.current++;
      setOutcomes({});
      setUnavailable(false);
      setRefetching(false);
      setSettled(false);
      return;
    }
    void refetch();
    // `refetch` is intentionally omitted: its identity only changes with
    // `userId`, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, userId]);

  const health = useMemo<CalendarHealth>(() => {
    // Auth still resolving is "we haven't asked yet", never an accusation.
    if (!enabled || !settled || authLoading) {
      return { state: 'loading' };
    }
    if (unavailable) {
      return { state: 'unavailable' };
    }
    return {
      state: 'ready',
      needsAttention: providerOrder
        .map((type) => outcomes[type]?.integration)
        .filter((integration): integration is IntegrationNeedingAttention =>
          Boolean(integration)
        ),
      uncertain: providerOrder.some(
        (type) => outcomes[type]?.suppressedOnGuess ?? false
      ),
    };
  }, [enabled, settled, authLoading, unavailable, outcomes, providerOrder]);

  return { health, refetching, refetch };
}
